import dynamic from "next/dynamic";
import { getRandom } from "../../utils/blogUtils";
import blogData from "../../blogs.json";
import Service2 from "../../components/services/Service2";
const Capabilities2 = dynamic(() => import("../../components/services/Capabilities2"), { ssr: false });
import SuccessStoryEmbed from "../../components/trust/SuccessStoryEmbed";
import MethodologyAnchor from "../../components/services/MethodologyAnchor";
import Head from "next/head";
import Link from "next/link";
import ServiceFAQ from "../../components/services/ServiceFAQ";
import ServiceEngagementLinks from "../../components/services/ServiceEngagementLinks";
import { CustomerSection } from "../../components/ai-agent-demo/CustomerSection";

const ContactUsHome = dynamic(() => import("../../components/homepage/ContactUsHome"), { ssr: false });
const Footer = dynamic(() => import("../../components/Footer"), { ssr: false });

qualityEng.title = "Quality Engineering & AI Testing Services | ITMTB";
qualityEng.desc =
  "ITMTB delivers test automation, AI agent testing, and QA management for enterprise software — covering non-deterministic AI systems, CI/CD integration, and production reliability.";
qualityEng.header_img = "https://www.itmtb.com/services/capability/quality.webp";
qualityEng.keywords =
  "QA consulting India enterprise, test automation India, AI agent testing India, quality engineering services India, testing non-deterministic systems, Playwright Cypress Selenium India, software testing as a service India";

const faqItems = [
  {
    q: "How is testing AI agents different from testing traditional software?",
    a: "Traditional software is deterministic: given the same input, you always get the same output. You can write tests that assert exact outputs and fail reliably when the code is wrong. AI agents are non-deterministic: the same prompt can produce different valid outputs, and the right output changes depending on context, retrieved data, and model version. This means traditional assertion-based testing breaks down. Instead, AI agent testing requires: prompt regression testing — verifying that a given input still produces an output within the acceptable behavioral envelope across model versions; tool-call accuracy testing — checking that agents invoke the right tools with the right parameters for a given scenario; output consistency testing — validating that outputs meet defined structural and semantic constraints rather than matching exactly; and latency and reliability testing — AI systems have higher p99 latency variance than deterministic code, and production SLAs require specific load testing. We've built testing frameworks for production AI agents covering all four areas.",
  },
  {
    q: "What testing frameworks do you use and how do you integrate with CI/CD?",
    a: "Framework selection depends on the application stack and what's already in your pipeline. For web application UI testing: Playwright is our default for modern web apps (fast, reliable, good debugging); Cypress for teams that want integrated component testing; Selenium where legacy infrastructure requires it. For API and backend testing: pytest with custom assertion layers for Python services; Jest or Vitest for Node.js APIs; Postman/Newman for contract testing in CI. For performance testing: k6 for developer-friendly load testing in CI pipelines; JMeter for complex enterprise scenarios. For AI agent testing: custom pytest frameworks with LLM evaluation harnesses, prompt regression suites, and behavioral constraint validators. CI/CD integration: we wire test suites into GitHub Actions, GitLab CI, Azure DevOps, or Jenkins depending on your existing setup — with test reporting that gates deployments when critical tests fail.",
  },
  {
    q: "What does end-to-end QA management involve and when does it make sense?",
    a: "End-to-end QA management means ITMTB acts as your QA function — owning strategy, test design, execution, and reporting — rather than just providing testing resources. It makes sense when: your internal team is small and QA expertise is expensive to hire full-time; you're scaling a product quickly and need QA capacity that can ramp without long hiring cycles; or QA on your current project is an afterthought and you need a structured reset. What's included: test strategy design (what to test, at what coverage level, with what automation ratio), test framework selection and setup, manual and automated test creation for new features, regression suite maintenance, and defect reporting integrated into your team's workflow. We have managed QA for a Series-A delivery startup whose tech debt was causing production failures, clearing the backlog and establishing a release process that allowed regular shipping without incidents.",
  },
  {
    q: "How do you test for performance and reliability in cloud-native applications?",
    a: "Performance testing for cloud-native systems is different from traditional load testing because auto-scaling makes the system behave differently under load than it does at rest. We test at multiple layers: application-level load testing (k6, JMeter) to establish latency profiles at 1×, 5×, and 10× expected load; database query profiling under concurrent load to identify slow queries that don't appear in unit tests; cold-start behaviour for serverless and containerised components (latency spike on first request after idle); and AI inference endpoint latency under concurrent requests (p50, p90, p99 matter separately for AI workloads). Outputs: a performance baseline report, identified bottlenecks with proposed fixes, and a load test suite you can re-run in CI to catch regressions before they hit production.",
  },
  {
    q: "How does ITMTB price an engagement?",
    a: "Pricing follows the engagement model. Discovery sprints are fixed-price for a fixed timebox. Build engagements are scoped after discovery — fixed-scope, time-and-materials, or outcome-linked. Managed services are billed monthly with defined SLAs. Total cost depends on scope, integration complexity, QA depth, and post-launch support — we publish a transparent breakdown for buyers evaluating their options.",
    link: "/blog/custom-software-development-cost-india",
    linkLabel: "Read the cost guide",
  },
  {
    q: "What does an engagement with ITMTB look like?",
    a: "Every engagement starts with a fixed-scope, fixed-price discovery sprint — typically 2 weeks. You leave with a written roadmap, risk register, cost estimate, and timeline before committing to a build. We then offer four engagement models: fixed-scope builds, time-and-materials, outcome-linked retainers, and managed services post-launch. If discovery doesn't justify proceeding, you can stop there — no obligation.",
    link: "/itmtb-how-we-work",
    linkLabel: "See how we work",
  },
];
qualityEng.faq = faqItems;

function qualityEng() {
  let insights = blogData.filter(item => !["case-study", "tech"].includes(item?.meta?.category));
  let caseStudies = blogData.filter(item => item?.meta?.category === "case-study");

  let filteredInsights = insights.filter(item => item?.meta?.refer?.includes("Quality Engineering"));
  let filteredCaseStudies = caseStudies.filter(item => item?.meta?.refer?.includes("Quality Engineering"));

  if (filteredInsights.length === 1) {
    insights = [...filteredInsights, ...getRandom(insights.filter(item => !filteredInsights.includes(item)), 1)];
  } else if (filteredInsights.length >= 2) {
    insights = getRandom(filteredInsights, 2);
  } else {
    insights = getRandom(insights, 2);
  }

  if (filteredCaseStudies.length === 1) {
    caseStudies = [...filteredCaseStudies, ...getRandom(caseStudies.filter(item => !filteredCaseStudies.includes(item)), 1)];
  } else if (filteredCaseStudies.length >= 2) {
    caseStudies = getRandom(filteredCaseStudies, 2);
  } else {
    caseStudies = getRandom(caseStudies, 2);
  }

  const subServices = [
    {
      img: null,
      key: "Testing AI Agents and Non-Deterministic Systems",
      title: "You can't assert exact outputs from an AI agent. You test behavioral envelopes — we've built the frameworks to do it.",
      text: "Testing a production AI system requires different strategies than testing deterministic software. We build testing frameworks covering prompt regression (verifying outputs stay within the acceptable behavioral envelope across model versions), tool-call accuracy (checking agents invoke the right tools with the right parameters), output consistency (structural and semantic constraint validation), and latency bounds under load.\n\nWe've built these frameworks for production AI agents deployed by ITMTB clients. The same infrastructure validates both the model layer and the application logic that wraps it.",
    },
    {
      img: null,
      key: "Test Automation Strategy and Infrastructure",
      title: "The right automation strategy: what to automate, at what level, with what framework — and what not to automate.",
      text: "We design automation strategies that match your team's capacity and your product's release cadence. Framework selection based on your stack: Playwright or Cypress for web UIs, pytest for Python services, Jest/Vitest for Node.js, k6 for load testing.\n\nCI/CD integration: test suites wired into GitHub Actions, GitLab CI, Azure DevOps, or Jenkins — with failure gates that block deployments when critical tests fail. We also set test coverage targets that are meaningful rather than arbitrary: coverage of the flows that matter, not 100% line coverage of utility functions.",
    },
    {
      img: null,
      key: "QA Management and Execution",
      title: "We act as your QA function — owning strategy, test design, execution, and reporting.",
      text: "For teams where QA is an afterthought or where hiring a full-time QA lead isn't justified, we manage the entire QA function as an extension of your team. This includes: test strategy design, manual and automated test creation for new features, regression suite maintenance, and defect reporting integrated into your team's workflow.\n\nFor a Series-A delivery startup whose tech debt was causing production failures, we cleared the backlog, established a structured release process, and reduced production incidents to near-zero within two months.",
    },
    {
      img: null,
      key: "Performance, Load, and API Testing",
      title: "Slow queries that run frequently are expensive. Latency regressions in production AI systems are silent until they're not.",
      text: "We test at multiple layers: application-level load testing (k6, JMeter) at 1×, 5×, and 10× expected load; database query profiling under concurrent load; cold-start behaviour for serverless and containerised components; and AI inference endpoint latency (p50, p90, p99 profiled separately).\n\nFor API and microservices: contract testing to catch schema drift between services, chaos engineering for failure injection, and integration tests that run against real dependencies rather than mocks.",
    },
  ];

  return (
    <div>
      <Head>
        <script
          type="application/ld+json"
          dangerouslySetInnerHTML={{
            __html: JSON.stringify({
              "@context": "https://schema.org",
              "@type": "ProfessionalService",
              name: "Quality Engineering & AI Testing Services India",
              url: "https://www.itmtb.com/services/quality-engineering",
              image: "https://www.itmtb.com/services/capability/quality.webp",
              description:
                "ITMTB delivers test automation, AI agent testing, and QA management for enterprise software — covering non-deterministic AI systems, CI/CD integration, and production reliability.",
              provider: {
                "@type": "Organization",
                name: "ITMTB Technologies",
                url: "https://www.itmtb.com",
                logo: "https://www.itmtb.com/logo.webp",
              },
              areaServed: [{ "@type": "Place", name: "India" }],
              serviceType: [
                "QA Consulting India",
                "Test Automation India",
                "AI Agent Testing",
                "Quality Engineering Services",
                "Testing Non-Deterministic AI Systems",
                "Playwright Cypress Selenium Consulting",
                "Software Testing as a Service India",
                "CI/CD Quality Integration",
              ],
              sameAs: [
                "https://www.linkedin.com/company/itmtb-technologies/",
                "https://x.com/ITMTBTech",
                "https://www.instagram.com/itmtbtech/",
              ],
            }),
          }}
        />
      </Head>

      <Service2
        heading="Quality Engineering and AI Testing"
        paragraph={`AI agents that work in demos fail in production for the same reason traditional software does: they weren't tested for the conditions they'd actually face. Non-deterministic AI systems need different testing strategies — and most QA practices weren't designed for them.

We test AI agents for production: prompt regression, tool-call accuracy, output consistency, and latency bounds under load. For the rest of your software stack, we apply mature automation, performance testing, and QA management that keeps release cycles fast without accumulating the technical debt that causes outages later.

For a Series-A hyperlocal delivery startup, we cleared a tech debt backlog that was causing production failures and established a release process that allowed regular shipping without incidents.`}
        bgPath="/services/capability/quality.webp"
        primaryCta={{ label: "Talk to us →", href: "/contact-us" }}
      />

      <SuccessStoryEmbed
        referTag="Quality Engineering"
        maxItems={3}
        heading="Selected work in quality engineering"
      />

      <CustomerSection title="Trusted by" />

      <MethodologyAnchor />

      <Capabilities2
        dataList={subServices}
        bg="bg-[#402A6A]"
        insights={[...caseStudies, ...insights]}
        hideFooter
        hideCardSubtitle
      />

      <section className="px-8 md:px-[60px] py-8 bg-white border-t border-gray-100">
        <p className="max-w-4xl text-sm text-gray-500 leading-relaxed">
          Quality engineering is one of the specialised practices within our broader{" "}
          <Link href="/services/custom-software-development">
            <a className="text-indigo-600 font-medium hover:text-indigo-800 transition-colors">custom software development</a>
          </Link>{" "}
          capability — the QA and release governance that ships alongside every build.
        </p>
      </section>

      <ServiceFAQ items={faqItems} />

      <ServiceEngagementLinks />

      <ContactUsHome />

      <Footer />
    </div>
  );
}

export default qualityEng;
